import * as df from 'date-fns';
import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';

import InputBookingWaitingList from './InputBookingWaitingList';

import BooksSubmitButton from '../../../../components/books/common/buttons/BooksSubmitButton';
import BooksDateCalendarHeader from '../../../../components/books/common/calendar/BooksDateCalendarHeader';
import BooksDateCalendarMonth, {
  BooksDateCalendarDay,
} from '../../../../components/books/common/calendar/BooksDateCalendarMonth';
import BooksSelectTimeEvent from '../../../../components/books/common/inputs/BooksSelectTimeEvent';
import BooksSelectTimeInstructor from '../../../../components/books/common/inputs/BooksSelectTimeInstructor';
import BooksLoadingMask from '../../../../components/books/common/loadings/BooksLoadingMask';
import BooksLabeledContainer from '../../../../components/books/model1/BooksLabeledContainer';

import { getCalendarDate, radioBehaviorByCheckbox } from './helper';

import * as IBooks from '../../../../interfaces/merchants/resources/books';

import css from './InputSchoolCalendarDateTimePicker.module.scss';

interface Props {
  postable: boolean;
  selection: 'single' | 'multiple';
  baseDate: Date;
  timeSlots: { [date: string]: IBooks.TimeSlot[] };
  showVacancy: boolean;
  onChange: (timeSlots: IBooks.TimeSlot[]) => void;
  onChangeBaseDate: (baseDate: Date) => void;
  selectedTimeSlots: IBooks.TimeSlot[];
  attendees?: number;
  waitingList?: {
    list: number[];
    onAddToWaitingList: (timeSlot: IBooks.TimeSlot) => Promise<void>;
    loggedIn: boolean;
    errors?: IBooks.WaitingListErrors;
  };
}

interface DateType {
  date: number;
  iso: string;
  type: string;
}

interface State {
  selectedDate?: string;
  selectedTimeSlots: IBooks.TimeSlot[];
  selectedWaitingTimeSlot?: IBooks.TimeSlot;
  calendar: {
    baseDate: Date;
    dates: DateType[];
  };
}

const isSchoolTimeSlot = (t: IBooks.TimeSlot): t is IBooks.SchoolTimeSlot =>
  t.hasOwnProperty('instructors');

class InputSchoolCalendarDateTimePicker extends React.Component<
  Props & InjectedIntlProps,
  State
> {
  constructor(props: Props & InjectedIntlProps) {
    super(props);
    const { selectedTimeSlots, baseDate } = this.props;
    this.state = {
      selectedDate:
        selectedTimeSlots.length === 0 ? undefined : selectedTimeSlots[0].date,
      selectedTimeSlots: selectedTimeSlots.concat(),
      calendar: { baseDate, dates: [] },
    };
    this.handleClickNext = this.handleClickNext.bind(this);
    this.handleClickPrev = this.handleClickPrev.bind(this);

    this.handleAddToWaitingList = this.handleAddToWaitingList.bind(this);

    this.handleClickDate = this.handleClickDate.bind(this);
    this.handleClickTimeSlot = this.handleClickTimeSlot.bind(this);
    this.handleClickCommit = this.handleClickCommit.bind(this);
    this.handleReturnCalendar = this.handleReturnCalendar.bind(this);
  }

  public async componentDidMount() {
    this.fillCalendarPropsBy(this.state.calendar.baseDate);
  }

  private handleClickNext() {
    const { baseDate } = this.state.calendar;
    this.fillCalendarPropsBy(df.addMonths(baseDate, 1));
  }

  private handleClickPrev() {
    const { baseDate } = this.state.calendar;
    this.fillCalendarPropsBy(df.subMonths(baseDate, 1));
  }

  private handleAddToWaitingList(e: React.MouseEvent<HTMLButtonElement>) {
    const { timeSlots, waitingList } = this.props;
    if (!waitingList) {
      return;
    }
    const { value } = e.currentTarget;
    Object.values(timeSlots).forEach(_timeSlots => {
      const timeSlot = _timeSlots.find(t => t.id.toString() === value);
      if (timeSlot) {
        if (!waitingList.loggedIn) {
          this.setState({ selectedWaitingTimeSlot: timeSlot });
          return;
        }
        waitingList.onAddToWaitingList(timeSlot);
        return;
      }
    });
  }
  private handleReturnCalendar() {
    this.setState({ selectedWaitingTimeSlot: undefined });
  }

  private async fillCalendarPropsBy(baseDate: Date) {
    const dates = getCalendarDate(baseDate);
    this.setState({
      ...this.state,
      calendar: { baseDate, dates },
    });
    this.props.onChangeBaseDate(baseDate);
  }

  private handleClickDate(e: React.MouseEvent<HTMLButtonElement>) {
    const { value } = e.currentTarget;
    this.setState({
      ...this.setState,
      selectedDate: value,
    });
  }

  private newSelectedTimeSlots(
    checked: boolean,
    newTimeSlot: IBooks.TimeSlot,
  ): IBooks.TimeSlot[] {
    const { selection } = this.props;
    const { selectedTimeSlots } = this.state;

    return radioBehaviorByCheckbox(
      selection,
      checked,
      selectedTimeSlots,
      newTimeSlot,
    );
  }

  private handleClickTimeSlot(e: React.ChangeEvent<HTMLInputElement>) {
    const { value, checked } = e.currentTarget;
    const { timeSlots } = this.props;
    const { selectedDate } = this.state;

    const timeSlot = timeSlots[selectedDate!].find(
      t => t.id.toString() === value,
    );

    if (!timeSlot) {
      throw new Error(
        `timeSlot could not be found: value: ${value}, date: ${selectedDate}`,
      );
    }

    this.setState({
      ...this.setState,
      selectedTimeSlots: this.newSelectedTimeSlots(checked, timeSlot),
    });
  }

  private handleClickCommit() {
    const { selectedTimeSlots } = this.state;
    this.props.onChange(selectedTimeSlots);
  }

  public render() {
    const { postable, timeSlots, intl, waitingList, showVacancy } = this.props;
    const attendees = this.props.attendees || 1;
    const {
      selectedDate,
      selectedTimeSlots,
      selectedWaitingTimeSlot,
      calendar,
    } = this.state;
    const timeSlotCandidates = selectedDate ? timeSlots[selectedDate] : [];

    return selectedWaitingTimeSlot ? (
      <InputBookingWaitingList
        timeSlot={selectedWaitingTimeSlot}
        errors={waitingList!.errors}
        onAddToWaitingList={waitingList!.onAddToWaitingList}
        onReturnSelectDateTime={this.handleReturnCalendar}
        postable={postable}
      />
    ) : (
      <BooksLoadingMask isLoading={!postable}>
        <div className={css.InputSchoolCalendarDateTimePicker}>
          <BooksDateCalendarHeader
            baseDate={intl.formatDate(df.format(calendar.baseDate), {
              year: 'numeric',
              month: 'long',
              day: undefined,
            })}
            onClickPrev={this.handleClickPrev}
            onClickNext={this.handleClickNext}
            prevButtonDisabled={df.isThisMonth(calendar.baseDate)}
          />
          <BooksDateCalendarMonth>
            {calendar.dates.map((date, index) => (
              <BooksDateCalendarDay
                key={index}
                value={date.iso}
                date={date.date}
                onClick={this.handleClickDate}
                disabled={
                  date.type !== 'current' ||
                  (df.isPast(date.iso) && !df.isToday(date.iso)) ||
                  !timeSlots[date.iso]
                }
                current={date.type === 'current'}
                selected={selectedDate === date.iso}
              />
            ))}
          </BooksDateCalendarMonth>
          {selectedDate && timeSlotCandidates ? (
            <BooksLabeledContainer
              label={`${intl.formatMessage({
                id: 'books.formsLabel.booksTime',
              })} (${intl.formatMessage({
                id: 'books.forms.pleaseSelectTime',
              })})`}
            >
              {timeSlotCandidates.map((timeSlot: IBooks.TimeSlot) =>
                isSchoolTimeSlot(timeSlot) ? (
                  <BooksSelectTimeInstructor
                    key={timeSlot.id}
                    value={timeSlot.id}
                    checked={
                      !!selectedTimeSlots.find(t => t.id === timeSlot.id)
                    }
                    onChange={this.handleClickTimeSlot}
                    startTime={timeSlot.startTime}
                    endTime={timeSlot.endTime}
                    vacancy={timeSlot.vacancy}
                    showVacancy={showVacancy}
                    instructors={timeSlot.instructors.map(i => i.name)}
                  />
                ) : (
                  <BooksSelectTimeEvent
                    key={timeSlot.id}
                    value={timeSlot.id}
                    postable={postable}
                    checked={
                      !!selectedTimeSlots.find(t => t.id === timeSlot.id)
                    }
                    onChange={this.handleClickTimeSlot}
                    startTime={timeSlot.startTime}
                    endTime={timeSlot.endTime}
                    vacancy={timeSlot.vacancy}
                    showVacancy={showVacancy}
                    attendees={attendees}
                    waitingListProvided={!!waitingList}
                    setNotification={
                      waitingList
                        ? !!waitingList.list.find(i => i === timeSlot.id)
                        : false
                    }
                    onClickSetNotification={this.handleAddToWaitingList}
                  />
                ),
              )}
            </BooksLabeledContainer>
          ) : (
            <BooksLabeledContainer
              label={intl.formatMessage({
                id: 'books.formsLabel.booksTime',
              })}
              contentsPadding={true}
            >
              <span
                className={css.InputSchoolCalendarDateTimePicker__PlaceHolder}
              >
                {intl.formatMessage({
                  id: 'books.forms.pleaseSelectDate',
                })}
              </span>
            </BooksLabeledContainer>
          )}
          <BooksSubmitButton
            disabled={!selectedDate || selectedTimeSlots.length === 0}
            onClick={this.handleClickCommit}
          >
            {intl.formatMessage({ id: 'books.submitLabel.addThisDateAndTime' })}
          </BooksSubmitButton>
        </div>
      </BooksLoadingMask>
    );
  }
}

// TODO: schoolだけでもないのでリネームする
export default injectIntl(InputSchoolCalendarDateTimePicker);
